import React, { useState } from "react";
import { Bell, CheckCircle2, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { trackFormSubmit } from "../lib/tracking";
import { SUPPORT_RATIOS, ACCESSIBILITY_TAGS } from "../data/propertyModel";

const STATES = ["VIC", "NSW", "QLD", "WA", "SA", "ACT", "TAS", "NT"];

/**
 * Compact vacancy-alert subscription form.
 *
 * `defaults` lets a page pre-fill filter context (e.g. the current /vacancies
 * filters, or a specific state/city page). Users can override before submitting.
 */
export default function VacancyAlertForm({ defaults = {}, testidPrefix = "vac-alert" }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    role: defaults.role || "family",
    state: defaults.state || "",
    support_ratio: defaults.support_ratio || "",
    single_storey: defaults.single_storey || false,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      setErr("Please add your name and email so we can reach you.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const accessibility = form.single_storey ? ["single-storey"] : [];
      await api.post("/vacancy-alerts", {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone || null,
        role: form.role,
        state: form.state || null,
        support_ratios: form.support_ratio ? [form.support_ratio] : [],
        accessibility,
        notes: form.notes || null,
      });
      trackFormSubmit("vacancy_alert", { state: form.state, ratio: form.support_ratio });
      setDone(true);
    } catch (ex) {
      setErr("Sorry — we couldn't save that just now. Please try again or call 1300 380 692.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="alpha-card p-6" data-testid={`${testidPrefix}-done`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[color:var(--alpha-green-50)] text-[color:var(--alpha-green-700)] flex items-center justify-center"><CheckCircle2 className="w-5 h-5" /></div>
          <h3 className="font-display text-lg font-semibold">You're on the alert list.</h3>
        </div>
        <p className="mt-3 text-sm text-[color:var(--alpha-mute)]">We'll email {form.email} the moment a home matching your criteria becomes available. In the meantime you can also{" "}
          <a href="/referrals/participant" className="text-[color:var(--alpha-purple-700)] font-semibold hover:underline">make a referral</a>{" "}or call 1300 380 692.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="alpha-card p-6" data-testid={`${testidPrefix}-form`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center"><Bell className="w-5 h-5" /></div>
        <div>
          <h3 className="font-display text-lg font-semibold">Get notified when a matching home opens</h3>
          <p className="text-xs text-[color:var(--alpha-mute)]">One email per matching vacancy. No marketing spam.</p>
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <Input placeholder="Your name" value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} required data-testid={`${testidPrefix}-name`} />
        <Input type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required data-testid={`${testidPrefix}-email`} />
      </div>

      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        <Select value={form.state || "any"} onValueChange={(v) => setForm((f) => ({ ...f, state: v === "any" ? "" : v }))}>
          <SelectTrigger className="h-11" data-testid={`${testidPrefix}-state`}><SelectValue placeholder="Any state" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any state</SelectItem>
            {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={form.support_ratio || "any"} onValueChange={(v) => setForm((f) => ({ ...f, support_ratio: v === "any" ? "" : v }))}>
          <SelectTrigger className="h-11" data-testid={`${testidPrefix}-ratio`}><SelectValue placeholder="Any support ratio" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any support ratio</SelectItem>
            {SUPPORT_RATIOS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <label className="mt-3 flex items-center gap-3 text-sm cursor-pointer">
        <input type="checkbox" checked={form.single_storey} onChange={(e) => setForm((f) => ({ ...f, single_storey: e.target.checked }))} data-testid={`${testidPrefix}-single-storey`} className="w-4 h-4 accent-[color:var(--alpha-purple-700)]" />
        <span>Only notify me about single-storey / step-free homes</span>
      </label>

      {err && <div className="mt-3 text-sm text-red-600" data-testid={`${testidPrefix}-error`}>{err}</div>}

      <button type="submit" disabled={busy} className="mt-5 alpha-btn-primary w-full justify-center" data-testid={`${testidPrefix}-submit`}>
        {busy ? "Saving…" : (<>Set up alert <ArrowRight className="w-4 h-4" /></>)}
      </button>
      <p className="mt-3 text-[11px] text-[color:var(--alpha-mute)]">You can unsubscribe anytime — a link is included in every alert.</p>
    </form>
  );
}
